import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { Anchor, ShieldAlert, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function DeleteApprove() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [state, setState] = useState("loading"); // loading | review | confirming | done | error
  const [info, setInfo] = useState(null);
  const [otp, setOtp] = useState("");
  const [err, setErr] = useState(null);
  const [step, setStep] = useState("review"); // review | areYouSure

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) {
        setState("error");
        setErr("Missing approval token. Open the link from the email exactly as sent.");
        return;
      }
      try {
        const { data } = await api.get(`/yachts/delete-approve/info`, { params: { token } });
        if (!alive) return;
        setInfo(data);
        setState("review");
      } catch (e) {
        if (!alive) return;
        setErr(fmtErr(e));
        setState("error");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const proceedToConfirm = () => {
    if (info?.requires_otp && otp.trim().length < 6) {
      setErr("Enter the 6-digit verification code from the email.");
      return;
    }
    setErr(null);
    setStep("areYouSure");
  };

  const confirmDelete = async () => {
    setState("confirming");
    setErr(null);
    try {
      const body = { token };
      if (info?.requires_otp) body.otp = otp.trim();
      const { data } = await api.post(`/yachts/delete-approve/confirm`, body);
      setInfo((p) => ({ ...p, yacht_name: data.yacht_name }));
      setState("done");
    } catch (e) {
      setErr(fmtErr(e));
      setState("review");
      // If the OTP was wrong, leave the user on the review screen to retry
      setStep("review");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="max-w-xl mx-auto px-6 pt-12 pb-16">
        <Link to="/" className="inline-flex items-center gap-2 mb-8 text-ink">
          <div className="w-7 h-7 rounded-md bg-ink text-white grid place-items-center">
            <Anchor className="h-4 w-4" />
          </div>
          <span className="font-display text-[17px] font-medium">Seavanto</span>
        </Link>

        <div className="rounded-lg border border-zinc-200 bg-white p-8 shadow-sm" data-testid="delete-approve-card">
          {state === "loading" && (
            <div className="flex items-center gap-3 text-zinc-600">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Loading deletion request…</span>
            </div>
          )}

          {state === "error" && (
            <div data-testid="delete-approve-error">
              <div className="flex items-center gap-2 text-red-700 mb-2">
                <AlertTriangle className="h-5 w-5" />
                <h1 className="font-display text-xl font-medium">Approval unavailable</h1>
              </div>
              <p className="text-sm text-zinc-700">{err}</p>
              <Link to="/" className="text-sm text-ink underline mt-4 inline-block">Back to Seavanto</Link>
            </div>
          )}

          {state !== "loading" && state !== "error" && state !== "done" && info && step === "review" && (
            <div>
              <div className="flex items-center gap-2 text-red-700 mb-2">
                <ShieldAlert className="h-5 w-5" />
                <h1 className="font-display text-xl font-medium">Yacht deletion approval</h1>
              </div>
              <p className="text-sm text-zinc-600">
                Hi {info.approver_name}, <strong className="text-ink">{info.requester_name}</strong>{" "}
                ({info.requester_role}) has requested to permanently delete a yacht. Review the
                details below before approving.
              </p>

              <div className="mt-6 rounded-md border border-zinc-200 bg-zinc-50 p-4 space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-zinc-500">Yacht</span>
                  <span className="font-medium text-ink truncate" data-testid="approval-yacht-name">{info.yacht_name}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-zinc-500">Requested by</span>
                  <span className="font-medium text-ink truncate">{info.requester_name}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-zinc-500">Your role</span>
                  <span className="font-medium text-ink uppercase tracking-wider text-xs">{info.approver_role}</span>
                </div>
              </div>

              <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                Approving will permanently delete this yacht and all its inventory,
                maintenance, tasks, logbook, petty cash, documents and photos. Crew accounts
                are preserved. <strong>This cannot be undone.</strong>
              </div>

              {info.requires_otp && (
                <div className="mt-5">
                  <Label htmlFor="otp">Verification code</Label>
                  <p className="text-xs text-zinc-500 mb-2">
                    Enter the 6-digit code from the approval email.
                  </p>
                  <Input
                    id="otp"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    className="tracking-[0.4em] text-center text-lg font-mono"
                    data-testid="approval-otp-input"
                  />
                </div>
              )}

              {err && <p className="mt-3 text-sm text-red-600" data-testid="approval-error">{err}</p>}

              <div className="mt-6 flex items-center justify-end gap-2">
                <Link to="/" className="text-sm text-zinc-600 underline">
                  Reject (close window)
                </Link>
                <Button
                  onClick={proceedToConfirm}
                  disabled={info.requires_otp && otp.length < 6}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="approval-continue"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {state !== "done" && info && step === "areYouSure" && (
            <div>
              <div className="flex items-center gap-2 text-red-700 mb-2">
                <AlertTriangle className="h-5 w-5" />
                <h1 className="font-display text-xl font-medium">Are you absolutely sure?</h1>
              </div>
              <p className="text-sm text-zinc-700">
                This will permanently delete <strong>{info.yacht_name}</strong> and all of its
                operational data. Crew accounts will not be deleted. There is no undo.
              </p>

              {err && <p className="mt-3 text-sm text-red-600">{err}</p>}

              <div className="mt-6 flex items-center justify-end gap-2">
                <Button variant="outline" onClick={() => setStep("review")} data-testid="approval-back">
                  Go back
                </Button>
                <Button
                  onClick={confirmDelete}
                  disabled={state === "confirming"}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="approval-final-confirm"
                >
                  {state === "confirming" ? "Deleting…" : "Yes, delete permanently"}
                </Button>
              </div>
            </div>
          )}

          {state === "done" && (
            <div data-testid="approval-done">
              <div className="flex items-center gap-2 text-emerald-700 mb-2">
                <CheckCircle2 className="h-5 w-5" />
                <h1 className="font-display text-xl font-medium">Deletion approved</h1>
              </div>
              <p className="text-sm text-zinc-700">
                <strong>{info?.yacht_name}</strong> has been permanently deleted from Seavanto.
                The requester has been notified.
              </p>
              <Link to="/" className="text-sm text-ink underline mt-4 inline-block">Back to Seavanto</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
